import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { FormsModule } from '@angular/forms';
import { MaterialModule } from '@app/material.module';

export interface CedisDiffRow {
  sapMaterial: string;
  description?: string;
  batch?: string;
  expiration?: string;
  quantityBefore?: number | null;
  quantityAfter?: number | null;
}

export interface CedisDiffData {
  /** Contra qué corte se compara (fecha · archivo), o vacío si es el primero. */
  comparedWith: string;
  fileName: string;
  /** Fecha del corte (AAAA-MM-DD); se puede corregir antes de guardar. */
  snapshotDate: string;
  rowCount: number;
  added: CedisDiffRow[];
  removed: CedisDiffRow[];
  changed: CedisDiffRow[];
  unchanged: number;
  skippedRows: number;
}

/**
 * Previsualización del diff de un corte de SAP contra el corte anterior.
 *
 * El corte es un REEMPLAZO completo (no suma): este diálogo es la última
 * parada antes de aplicar, y muestra exactamente qué entra, qué sale y qué
 * cambia de cantidad, para que quien carga pueda detectar un Excel
 * equivocado (almacén incorrecto, archivo viejo) antes de pisar el corte.
 */
@Component({
  standalone: true,
  selector: 'app-cedis-diff-dialog',
  imports: [CommonModule, FormsModule, MatDialogModule, MaterialModule],
  template: `
    <h2 mat-dialog-title>Previsualización del corte</h2>
    <mat-dialog-content>
      <div class="meta">
        <mat-form-field appearance="outline" subscriptSizing="dynamic">
          <mat-label>Fecha del corte</mat-label>
          <input matInput type="date" [(ngModel)]="data.snapshotDate" required>
        </mat-form-field>
        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="meta__file">
          <mat-label>Nombre del archivo</mat-label>
          <input matInput [(ngModel)]="data.fileName" maxlength="160" required>
        </mat-form-field>
      </div>
      <p class="context">
        {{ data.rowCount }} renglones con todos los macrocentros, FSL y cuentas.
        Se guarda como un corte nuevo; los anteriores siguen disponibles en el filtro de archivo.
        @if (data.comparedWith) {<br>Comparado contra: <b>{{ data.comparedWith }}</b>}
      </p>

      <div class="totals">
        <span class="pill pill--add">{{ data.added.length }} altas</span>
        <span class="pill pill--rem">{{ data.removed.length }} bajas</span>
        <span class="pill pill--chg">{{ data.changed.length }} cambios de cantidad</span>
        <span class="pill">{{ data.unchanged }} sin cambio</span>
        @if (data.skippedRows > 0) {
          <span class="pill pill--warn">{{ data.skippedRows }} renglones ignorados (sin material o cantidad)</span>
        }
      </div>

      @if (data.added.length > 0) {
        <h3>Altas (no estaban en el corte anterior)</h3>
        <table class="diff">
          <tr><th>Material</th><th>Descripción</th><th>Lote</th><th>Caducidad</th><th class="num">Cantidad</th></tr>
          @for (r of data.added; track $index) {
            <tr><td>{{ r.sapMaterial }}</td><td>{{ r.description || '—' }}</td><td>{{ r.batch || '—' }}</td><td>{{ r.expiration || '—' }}</td><td class="num">{{ r.quantityAfter }}</td></tr>
          }
        </table>
      }

      @if (data.removed.length > 0) {
        <h3>Bajas (desaparecen del corte)</h3>
        <table class="diff">
          <tr><th>Material</th><th>Descripción</th><th>Lote</th><th class="num">Cantidad anterior</th></tr>
          @for (r of data.removed; track $index) {
            <tr><td>{{ r.sapMaterial }}</td><td>{{ r.description || '—' }}</td><td>{{ r.batch || '—' }}</td><td class="num">{{ r.quantityBefore }}</td></tr>
          }
        </table>
      }

      @if (data.changed.length > 0) {
        <h3>Cambios de cantidad</h3>
        <table class="diff">
          <tr><th>Material</th><th>Lote</th><th class="num">Antes</th><th class="num">Ahora</th></tr>
          @for (r of data.changed; track $index) {
            <tr><td>{{ r.sapMaterial }}</td><td>{{ r.batch || '—' }}</td><td class="num">{{ r.quantityBefore }}</td><td class="num">{{ r.quantityAfter }}</td></tr>
          }
        </table>
      }
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="ref.close(false)">Cancelar</button>
      <button mat-raised-button color="accent" [disabled]="!data.snapshotDate || !data.fileName.trim()"
              (click)="ref.close({ snapshotDate: data.snapshotDate, fileName: data.fileName.trim() })">Guardar corte</button>
    </mat-dialog-actions>
  `,
  styles: [`
    mat-dialog-content { max-height: 60vh; }
    .context { color: #555555; }
    .meta { display: flex; gap: 12px; flex-wrap: wrap; margin: 4px 0 8px; }
    .meta__file { flex: 1; min-width: 240px; }
    .totals { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px; }
    .pill { padding: 2px 10px; border-radius: 12px; background: #e8e8e8; font-size: 12px; }
    .pill--add { background: #edfcf3; color: #14853d; }
    .pill--rem { background: #ffdde3; color: #cc0025; }
    .pill--chg { background: #dde7ff; color: #100d78; }
    .pill--warn { background: #fde6d8; color: #953707; }
    h3 { font-size: 13px; margin: 14px 0 6px; color: #170f5f; }
    table.diff { width: 100%; border-collapse: collapse; font-size: 12px; }
    table.diff th { text-align: left; color: #555555; font-weight: 600; padding: 4px 6px; border-bottom: 1px solid #e0e0e0; }
    table.diff td { padding: 3px 6px; border-bottom: 1px solid #f0f0f0; }
    .num { text-align: right; }
  `],
})
export class CedisDiffDialogComponent {
  constructor(
    public ref: MatDialogRef<CedisDiffDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CedisDiffData,
  ) {}
}
